import React, { useEffect, useState } from "react";
import { message, Spin } from "antd";
import { useNavigate } from "react-router-dom";
import {
  UserOutlined, TransactionOutlined, DollarOutlined,
  WalletOutlined, RiseOutlined, BankOutlined,
  ClockCircleOutlined, ArrowRightOutlined,
  CheckCircleOutlined, CloseCircleOutlined,
  PhoneOutlined, MailOutlined, IdcardOutlined,
  UserAddOutlined, GiftOutlined, CopyOutlined,
  LinkOutlined, WhatsAppOutlined,
} from "@ant-design/icons";
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer,
} from "recharts";
import { UserGet, StaffTransactionsGet } from "../../../../Api/CoreApi";
import { formatDate, generateRefNumber } from "../../../Admin_Panel/Utils/formatters";
import "./StaffDashboard.css";

function StaffDashboard() {
  const navigate = useNavigate();
  const staffName = localStorage.getItem("username") || "Staff Member";
  const staffId = localStorage.getItem("user_id") || "0001";
  const staffContact = localStorage.getItem("contact") || "";
  const staffEmail = localStorage.getItem("email") || "";
  const staffRef = localStorage.getItem("refer") || "";  // ✅ Staff ka referral code
  
  const [loading, setLoading] = useState(true);
  const [staffProfileId, setStaffProfileId] = useState(null);

  const [stats, setStats] = useState({
    totalUsers: 0,        // ✅ Sirf staff ke users (added + referral)
    addedByStaff: 0,      // ✅ Direct added users
    referralUsers: 0,     // ✅ Referral se aaye users
    totalTransactions: 0,
    totalCommission: 0,
    pendingWithdrawal: 0,
    availableBalance: 0,
  });

  const [monthlyData, setMonthlyData] = useState([]);
  const [recentTransactions, setRecentTransactions] = useState([]);

  // ── ✅ Helper: Check if user added by this staff ─────────────
  const isAddedByStaff = (user) => {
    if (!user) return false;
    return user.added_by_staff && String(user.added_by_staff) === String(staffId);
  };

  // ── ✅ Helper: Check if user referred by this staff ───────────
  const isReferralUser = (user, currentStaffProfileId) => {
    if (!user) return false;
    
    // Agar already added by this staff, to referral nahi
    if (isAddedByStaff(user)) return false;

    const staffRefStr = String(staffRef || '').trim();
    const staffPidStr = String(currentStaffProfileId || '').trim();
    
    // Check refer_by field
    const userReferBy = String(user.refer_by || '').trim();
    if (userReferBy && staffRefStr && userReferBy === staffRefStr) return true;
    
    // Check old ref field (backward compatibility)
    const userRef = String(user.ref || '').trim();
    if (userRef && staffRefStr && userRef === staffRefStr) return true;
    if (userRef && staffPidStr && userRef === staffPidStr) return true;
    
    return false;
  };

  // ── ✅ Belongs to this staff (added OR referral) ─────────────
  const belongsToStaff = (user, currentStaffProfileId) => 
    isAddedByStaff(user) || isReferralUser(user, currentStaffProfileId);

  // ── Referral Helpers ─────────────────────────
  const getReferralCode = () =>
    localStorage.getItem("refer") || staffProfileId || "";

  const getReferralLink = () => {
    const code = getReferralCode();
    return `${window.location.origin}/User_Reg/${code}`;
  };

  const handleCopyLink = () => {
    const link = getReferralLink();
    navigator.clipboard.writeText(link)
      .then(() => message.success("✅ Referral link copied!"))
      .catch(() => {
        const ta = document.createElement("textarea");
        ta.value = link;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
        message.success("✅ Referral link copied!");
      });
  };

  const handleCopyCode = () => {
    const code = getReferralCode();
    if (!code) return message.warning("No referral code found");
    navigator.clipboard.writeText(code)
      .then(() => message.success("✅ Referral code copied!"))
      .catch(() => message.error("Copy failed"));
  };

  const handleShareWhatsApp = () => {
    const code = getReferralCode();
    const link = getReferralLink();
    const text = encodeURIComponent(
      `🎉 Join Matrimonial App!\n\nHello! I am ${staffName}.\n\nRegister using my referral link:\n${link}\n\nReferral Code: ${code}\n\n✅ Register now and find your perfect match!`
    );
    window.open(`https://wa.me/?text=${text}`, "_blank");
  };

  // ── ✅ Fetch Data (UPDATED) ───────────────────────────────
  const fetchData = async () => {
    try {
      setLoading(true);
      const [usersRes, txRes] = await Promise.all([
        UserGet(),
        StaffTransactionsGet(),
      ]);

      const allUsersOnly = Array.isArray(usersRes)
        ? usersRes.filter(u => u.role === "user")
        : [];
      const transactions = Array.isArray(txRes) ? txRes : [];

      // ✅ Current staff data
      const currentStaff = Array.isArray(usersRes)
        ? usersRes.find(u => String(u.id) === String(staffId))
        : null;
      
      const currentStaffProfileId = currentStaff?.profile_id || null;
      
      if (currentStaffProfileId) {
        setStaffProfileId(currentStaffProfileId);
      }

      // ✅ Filter — sirf is staff ke users
      const myUsers = allUsersOnly.filter(u => 
        belongsToStaff(u, currentStaffProfileId)
      );

      // ✅ Debug logs
      console.log("🔍 Staff Info:", { 
        staffId, 
        staffRef, 
        staffProfileId: currentStaffProfileId 
      });
     

      // ✅ Count breakdown
      const addedCount = myUsers.filter(isAddedByStaff).length;
      const referralCount = myUsers.filter(u => 
        isReferralUser(u, currentStaffProfileId)
      ).length;

      // Commission calculations
      const totalCommission = transactions
        .filter(t => t.type === "credit")
        .reduce((s, t) => s + (parseFloat(t.amount) || 0), 0);

      const completedWithdrawals = transactions
        .filter(t =>
          (t.type === "withdrawal" || t.type === "debit") &&
          (t.status === "completed" || t.status === "approved")
        )
        .reduce((s, t) => s + (parseFloat(t.amount) || 0), 0);

      const pendingWithdrawal = transactions
        .filter(t =>
          (t.type === "withdrawal" || t.type === "debit") &&
          t.status === "pending"
        )
        .reduce((s, t) => s + (parseFloat(t.amount) || 0), 0);

      // ✅ Set stats with filtered users
      setStats({
        totalUsers: myUsers.length,           // ✅ Sirf staff ke
        addedByStaff: addedCount,             // ✅ Direct added
        referralUsers: referralCount,         // ✅ Referral se
        totalTransactions: transactions.length,
        totalCommission,
        pendingWithdrawal,
        availableBalance: totalCommission - completedWithdrawals,
      });

      // Monthly withdrawals
      const months = [
        "Jan", "Feb", "Mar", "Apr", "May", "Jun",
        "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
      ];
      const monthlyTotals = {};
      months.forEach(m => (monthlyTotals[m] = 0));
      transactions.forEach(tx => {
        if (tx.type === "withdrawal" || tx.type === "debit") {
          const date = new Date(tx.created_date);
          if (!isNaN(date)) {
            const month = date.toLocaleString("en-US", { month: "short" });
            if (monthlyTotals[month] !== undefined)
              monthlyTotals[month] += parseFloat(tx.amount) || 0;
          }
        }
      });
      setMonthlyData(months.map(m => ({ month: m, amount: monthlyTotals[m] })));

      // Recent transactions
      const sorted = [...transactions].sort(
        (a, b) => new Date(b.created_date || 0) - new Date(a.created_date || 0)
      );
      setRecentTransactions(sorted.slice(0, 5));

    } catch (error) {
      console.error("❌ Fetch error:", error);
      message.error("Failed to fetch dashboard data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  if (loading) {
    return (
      <div className="sd-loading">
        <Spin size="large" />
        <p>Loading your dashboard...</p>
      </div>
    );
  }

  return (
    <div className="sd-page">

      {/* ===== STAFF PROFILE ===== */}
      <div className="sd-profile-card">
        <div className="sd-profile-left">
          <div className="sd-profile-avatar">
            {staffName.charAt(0).toUpperCase()}
          </div>
          <div className="sd-profile-info">
            <h2 className="sd-profile-name">
              Welcome, {staffName}! 👋
            </h2>
            <div className="sd-profile-meta">
              <span className="sd-profile-ref">
                <IdcardOutlined />
                {staffProfileId || `CRST${String(staffId).padStart(6, "0")}`}
              </span>
              {staffContact && (
                <span className="sd-profile-contact">
                  <PhoneOutlined /> {staffContact}
                </span>
              )}
              {staffEmail && (
                <span className="sd-profile-email">
                  <MailOutlined /> {staffEmail}
                </span>
              )}
            </div>
            <div className="sd-profile-role">
              <UserOutlined /> Matchmaker Staff
            </div>
          </div>
        </div>

        <div className="sd-balance-card">
          <div className="sd-balance-icon"><WalletOutlined /></div>
          <div className="sd-balance-content">
            <div className="sd-balance-label">Available Balance</div>
            <div className="sd-balance-amount">
              ₹{stats.availableBalance.toLocaleString("en-IN")}
            </div>
            <button
              className="sd-balance-btn"
              onClick={() => navigate("/staff/withdrawals")}
            >
              Withdraw Now <ArrowRightOutlined />
            </button>
          </div>
        </div>
      </div>

      {/* ===== REFERRAL CARD ===== */}
      <div className="sd-referral-card">
        <div className="sd-referral-link-section">
          <div className="sd-ref-icon-wrap link">
            <LinkOutlined />
          </div>
          <div className="sd-ref-text">
            <span className="sd-ref-title">Your Referral Link</span>
            <div className="sd-ref-link-box" title={getReferralLink()}>
              {getReferralLink()}
            </div>
          </div>
          <div className="sd-ref-actions">
            <button className="sd-copy-link-btn" onClick={handleCopyLink}>
              <CopyOutlined /> Copy Link
            </button>
            <button className="sd-whatsapp-btn" onClick={handleShareWhatsApp}>
              <WhatsAppOutlined /> Share
            </button>
          </div>
        </div>

        <div className="sd-ref-divider" />

        <div className="sd-referral-code-section">
          <div className="sd-ref-icon-wrap code">
            <GiftOutlined />
          </div>
          <div className="sd-ref-text">
            <span className="sd-ref-title">Referral Code</span>
            <div className="sd-ref-code-box">
              {getReferralCode() || "N/A"}
            </div>
          </div>
          <div className="sd-ref-actions">
            <button className="sd-copy-code-btn" onClick={handleCopyCode}>
              <CopyOutlined /> Copy
            </button>
          </div>
        </div>
      </div>

      {/* ===== STATS (UPDATED) ===== */}
      <div className="sd-stats-grid">
        
        {/* ✅ My Added Users - With Breakdown */}
        <div className="sd-stat-card" onClick={() => navigate("/staff/users")}>
          <div className="sd-stat-icon"><UserAddOutlined /></div>
          <div className="sd-stat-body">
            <span className="sd-stat-label">My Users</span>
            <span className="sd-stat-value">{stats.totalUsers}</span>
            <span className="sd-stat-link">View my users →</span>
          </div>
        </div>

        <div className="sd-stat-card" onClick={() => navigate("/staff/transactions")}>
          <div className="sd-stat-icon"><TransactionOutlined /></div>
          <div className="sd-stat-body">
            <span className="sd-stat-label">My Transactions</span>
            <span className="sd-stat-value">{stats.totalTransactions}</span>
            <span className="sd-stat-link">View all →</span>
          </div>
        </div>

        <div className="sd-stat-card">
          <div className="sd-stat-icon"><DollarOutlined /></div>
          <div className="sd-stat-body">
            <span className="sd-stat-label">Commission Earned</span>
            <span className="sd-stat-value">
              ₹{stats.totalCommission.toLocaleString("en-IN")}
            </span>
            <span className="sd-stat-trend">
              <RiseOutlined /> This month
            </span>
          </div>
        </div>

        <div className="sd-stat-card" onClick={() => navigate("/staff/withdrawals")}>
          <div className="sd-stat-icon"><ClockCircleOutlined /></div>
          <div className="sd-stat-body">
            <span className="sd-stat-label">Pending Withdrawals</span>
            <span className="sd-stat-value">
              ₹{stats.pendingWithdrawal.toLocaleString("en-IN")}
            </span>
            <span className="sd-stat-link">View withdrawals →</span>
          </div>
        </div>
      </div>

      {/* ===== CHART ===== */}
      <div className="sd-chart-card">
        <div className="sd-chart-header">
          <div className="sd-chart-title-block">
            <div className="sd-chart-icon"><RiseOutlined /></div>
            <div>
              <h3>Monthly Withdrawal Analysis</h3>
              <p>Your withdrawal trends</p>
            </div>
          </div>
          <div className="sd-chart-tag">
            Total: ₹{monthlyData
              .reduce((s, m) => s + m.amount, 0)
              .toLocaleString("en-IN")}
          </div>
        </div>
        <ResponsiveContainer width="100%" height={220}>
          <AreaChart
            data={monthlyData}
            margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
          >
            <defs>
              <linearGradient id="sdGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#ff0055" stopOpacity={0.4} />
                <stop offset="100%" stopColor="#ff0055" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
            <XAxis dataKey="month" axisLine={false} tickLine={false}
              tick={{ fill: "#888", fontSize: 11 }} />
            <YAxis axisLine={false} tickLine={false}
              tick={{ fill: "#888", fontSize: 11 }}
              tickFormatter={v => v >= 1000 ? `₹${v / 1000}K` : `₹${v}`} />
            <Tooltip
              formatter={v => [`₹${v.toLocaleString("en-IN")}`, "Amount"]}
              contentStyle={{ borderRadius: 8, border: "1px solid #f0f0f0", fontSize: 12 }}
            />
            <Area type="monotone" dataKey="amount" stroke="#ff0055"
              strokeWidth={2.5} fill="url(#sdGradient)"
              dot={{ fill: "#ff0055", r: 3 }} activeDot={{ r: 5 }} />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* ===== BOTTOM ROW ===== */}
      <div className="sd-bottom-row">
        <div className="sd-recent-card">
          <div className="sd-card-header">
            <h3>Recent Transactions</h3>
            <button onClick={() => navigate("/staff/transactions")}>
              View All →
            </button>
          </div>
          <div className="sd-recent-list">
            {recentTransactions.length === 0 ? (
              <div className="sd-empty">No recent transactions</div>
            ) : (
              recentTransactions.map((tx, i) => (
                <div key={tx.id || i} className="sd-recent-item">
                  <div className={`sd-recent-icon ${tx.type === "credit" ? "credit" : "debit"}`}>
                    {tx.type === "credit" ? <RiseOutlined /> : <WalletOutlined />}
                  </div>
                  <div className="sd-recent-info">
                    <div className="sd-recent-title">
                      {tx.type === "credit" ? "Commission Credit" : "Withdrawal"}
                    </div>
                    <div className="sd-recent-meta">
                      {generateRefNumber("transaction", tx.id)} •{" "}
                      {formatDate(tx.created_date)}
                    </div>
                  </div>
                  <div className="sd-recent-right">
                    <div className={`sd-recent-amount ${tx.type === "credit" ? "credit" : "debit"}`}>
                      {tx.type === "credit" ? "+" : "-"}
                      ₹{parseFloat(tx.amount || 0).toLocaleString("en-IN")}
                    </div>
                    <div className={`sd-recent-status ${
                      tx.status === "success" || tx.status === "completed" || tx.status === "approved"
                        ? "success"
                        : tx.status === "pending"
                        ? "pending"
                        : "failed"
                    }`}>
                      {tx.status === "success" || tx.status === "completed" || tx.status === "approved"
                        ? <CheckCircleOutlined />
                        : tx.status === "pending"
                        ? <ClockCircleOutlined />
                        : <CloseCircleOutlined />}
                      {" "}{tx.status}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        <div className="sd-quick-card">
          <div className="sd-card-header"><h3>Quick Navigation</h3></div>
          <div className="sd-quick-grid">
            <button className="sd-quick-btn" onClick={() => navigate("/staff/users")}>
              <UserOutlined /><span>My Users</span>
            </button>
            <button className="sd-quick-btn" onClick={() => navigate("/staff/transactions")}>
              <TransactionOutlined /><span>Transactions</span>
            </button>
            <button className="sd-quick-btn" onClick={() => navigate("/staff/bank")}>
              <BankOutlined /><span>Bank Details</span>
            </button>
            <button className="sd-quick-btn" onClick={() => navigate("/staff/withdrawals")}>
              <WalletOutlined /><span>Withdrawals</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default StaffDashboard;